import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { SmartTeamsAppUser } from '../../db/SmartTeamsAppUser.js';
import { SmartTeamsAppUserGroup } from '../../features/permissions/roles-and-permissions.js';
import {
  archiveSmartTeamsAppUser,
  createSmartTeamsAppUser,
  getSmartTeamsAppUsers,
  getSmartTeamsPartnerCompanies,
} from '../../api/user-queries.js';
import * as m from '../../localization/out/messages.js';

const USER_GROUP_OPTIONS = Object.values(SmartTeamsAppUserGroup);

const UserGroupBadge = (group: string | undefined) => {
  if (!group) return nothing;
  const colorMap: Record<string, string> = {
    Admin: 'badge-primary',
    FieldEngineer: 'badge-info',
    Customer: 'badge-success',
    PartnerManager: 'badge-warning',
  };
  const colorClass = colorMap[group] ?? 'badge-neutral';
  return html`<span class="badge badge-sm ${colorClass}">${group}</span>`;
};

const CreateUserDialog = stateful(({ onClose }: { onClose: () => void }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userGroup, setUserGroup] = useState<SmartTeamsAppUserGroup>(SmartTeamsAppUserGroup.FieldEngineer);
  const [primaryLanguage, setPrimaryLanguage] = useState('en');
  const [serviceNowFEId, setServiceNowFEId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const companies = useQuery(() => getSmartTeamsPartnerCompanies()({ _id: 1, name: 1 }), []);
  const companyOptions = companies.value ?? [];

  const handleCreate = async () => {
    setError('');
    if (!username || !email || !password) {
      setError(m.error_email_required());
      return;
    }
    setLoading(true);
    try {
      await createSmartTeamsAppUser(
        username,
        password,
        email,
        userGroup,
        primaryLanguage || undefined,
        serviceNowFEId || undefined,
        companyId || undefined,
      )({ _id: 1 });
      onClose();
    } catch (e: any) {
      setError(e.message || m.error_create_user());
    } finally {
      setLoading(false);
    }
  };

  return html`
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click=${onClose}>
      <div class="bg-base-100 rounded-lg shadow-xl p-6 w-full max-w-md mx-4" @click=${(e: Event) => e.stopPropagation()}>
        <h3 class="text-lg font-bold mb-4">${m.user_create_title()}</h3>

        <div class="flex flex-col gap-3">
          <div class="form-control">
            <label class="label"><span class="label-text">${m.user_username()}</span></label>
            <input type="text" class="input input-bordered input-sm" placeholder="${m.user_username_placeholder()}"
              .value=${username} @input=${(e: InputEvent) => setUsername((e.target as HTMLInputElement).value)} />
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">${m.user_email()}</span></label>
            <input type="email" class="input input-bordered input-sm" placeholder="${m.user_email_placeholder()}"
              .value=${email} @input=${(e: InputEvent) => setEmail((e.target as HTMLInputElement).value)} />
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">${m.user_password()}</span></label>
            <input type="password" class="input input-bordered input-sm" placeholder="${m.user_password_placeholder()}"
              .value=${password} @input=${(e: InputEvent) => setPassword((e.target as HTMLInputElement).value)} />
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">${m.user_group()}</span></label>
            <select class="select select-bordered select-sm" @change=${(e: Event) => setUserGroup((e.target as HTMLSelectElement).value as SmartTeamsAppUserGroup)}>
              ${USER_GROUP_OPTIONS.map(g => html`<option value=${g} ?selected=${g === userGroup}>${g}</option>`)}
            </select>
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">${m.user_language()}</span></label>
            <input type="text" class="input input-bordered input-sm" placeholder="${m.user_language_placeholder()}"
              .value=${primaryLanguage} @input=${(e: InputEvent) => setPrimaryLanguage((e.target as HTMLInputElement).value)} />
          </div>
          ${[SmartTeamsAppUserGroup.FieldEngineer, SmartTeamsAppUserGroup.Customer, SmartTeamsAppUserGroup.PartnerManager].includes(userGroup)
            ? html`
                <div class="form-control">
                  <label class="label"><span class="label-text">${m.user_servicenow_fe_id()}</span></label>
                  <input type="text" class="input input-bordered input-sm" placeholder="${m.user_servicenow_id_placeholder()}"
                    .value=${serviceNowFEId} @input=${(e: InputEvent) => setServiceNowFEId((e.target as HTMLInputElement).value)} />
                </div>
              `
            : nothing}
          <div class="form-control">
            <label class="label"><span class="label-text">${m.user_company()}</span></label>
            <select class="select select-bordered select-sm" @change=${(e: Event) => setCompanyId((e.target as HTMLSelectElement).value)}>
              <option value="" ?selected=${companyId === ''}>-</option>
              ${companyOptions.map(
                company =>
                  html`<option value=${company._id} ?selected=${company._id === companyId}>${company.name}</option>`,
              )}
            </select>
          </div>
        </div>

        ${error ? html`<div class="text-error text-sm mt-2">${error}</div>` : nothing}

        <div class="flex justify-end gap-2 mt-6">
          <button class="btn btn-ghost btn-sm" @click=${onClose}>${m.dialog_cancel()}</button>
          <button class="btn btn-primary btn-sm" ?disabled=${loading} @click=${handleCreate}>
            ${loading ? html`<span class="loading loading-spinner loading-sm"></span>` : m.user_create()}
          </button>
        </div>
      </div>
    </div>
  `;
});

export const UserManagementPage = stateful(() => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchText, setSearchText] = useState('');

  const companies = useQuery(() => getSmartTeamsPartnerCompanies()({ _id: 1, name: 1 }), []);

  const users = useQuery(
    () =>
      getSmartTeamsAppUsers(
        0,
        500,
        undefined,
      )({
        _id: 1,
        userId: 1,
        username: 1,
        email: 1,
        userGroup: 1,
        primaryLanguage: 1,
        archived: 1,
        serviceNowFEId: 1,
        companyId: 1,
      }),
    [],
  );

  if (users.loading) {
    return html`
      <div class="flex items-center justify-center h-full">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>
    `;
  }

  if (users.error) {
    return html`
      <div class="flex items-center justify-center h-full text-error">
        <p>${m.error_generic()}</p>
      </div>
    `;
  }

  const allUsers = users.value ?? [];
  const companyNamesById = new Map((companies.value ?? []).map(company => [company._id, company.name]));

  const filtered = searchText
    ? allUsers.filter(u => {
        const s = searchText.toLowerCase();
        return (
          u.username?.toLowerCase().includes(s) ||
          u.email?.toLowerCase().includes(s) ||
          u.userGroup?.toLowerCase().includes(s)
        );
      })
    : allUsers;

  const handleArchive = async (user: SmartTeamsAppUser) => {
    if (!confirm(m.user_archive_confirm({ username: user.username ?? '' }))) return;
    try {
      await archiveSmartTeamsAppUser(user._id)({ _id: 1 });
    } catch (e: any) {
      alert(e.message || m.error_generic());
    }
  };

  return html`
    <div class="p-4 flex flex-col gap-4 h-full">
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 class="text-2xl font-bold">${m.user_management_title()}</h1>
          <p class="text-sm text-base-content/60">${m.user_management_subtitle()}</p>
        </div>
        <button class="btn btn-primary btn-sm" @click=${() => setShowCreateDialog(true)}>
          <span class="icon-[material-symbols--add] text-lg"></span>
          ${m.user_create()}
        </button>
      </div>

      <!-- Search -->
      <label class="input input-bordered flex items-center gap-2">
        <span class="icon-[material-symbols--search] text-base-content/40"></span>
        <input
          type="text"
          class="grow"
          placeholder="${m.user_search_placeholder()}"
          .value=${searchText}
          @input=${(e: InputEvent) => setSearchText((e.target as HTMLInputElement).value)}
        />
        ${searchText
          ? html`
              <button class="btn btn-ghost btn-xs btn-circle" @click=${() => setSearchText('')}>
                <span class="icon-[material-symbols--close] text-sm"></span>
              </button>
            `
          : nothing}
      </label>

      <!-- Table -->
      ${filtered.length === 0
        ? html`
            <div class="flex flex-col items-center justify-center flex-1 text-base-content/50 gap-2 py-8">
              <span class="icon-[material-symbols--group-outline] text-4xl"></span>
              <p>${m.user_no_users()}</p>
            </div>
          `
        : html`
            <div class="overflow-x-auto flex-1 min-h-0">
              <table class="table table-sm table-pin-rows">
                <thead>
                  <tr>
                    <th>${m.user_username()}</th>
                    <th>${m.user_email()}</th>
                    <th>${m.user_company()}</th>
                    <th>${m.user_group()}</th>
                    <th>${m.user_language()}</th>
                    <th>${m.ticket_status()}</th>
                    <th>${m.user_servicenow_fe_id()}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  ${filtered.map(
                    user => html`
                      <tr class="hover:bg-base-200">
                        <td><span class="font-medium">${user.username ?? '-'}</span></td>
                        <td><span class="text-sm">${user.email ?? '-'}</span></td>
                        <td><span class="text-sm">${companyNamesById.get(user.companyId ?? '') ?? '-'}</span></td>
                        <td>${UserGroupBadge(user.userGroup ?? undefined)}</td>
                        <td><span class="text-sm">${user.primaryLanguage ?? '-'}</span></td>
                        <td>
                          ${user.archived
                            ? html`<span class="badge badge-sm badge-error">${m.user_archived()}</span>`
                            : html`<span class="badge badge-sm badge-ghost">${m.user_active()}</span>`}
                        </td>
                        <td>
                          ${user.serviceNowFEId
                            ? html`<span class="text-xs font-mono truncate max-w-[100px] block">${user.serviceNowFEId}</span>`
                            : html`<span class="text-base-content/30">-</span>`}
                        </td>
                        <td>
                          ${!user.archived
                            ? html`
                                <button class="btn btn-ghost btn-xs text-error" @click=${() => handleArchive(user)}>
                                  ${m.user_archive()}
                                </button>
                              `
                            : nothing}
                        </td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table>
            </div>
          `}

      ${showCreateDialog ? CreateUserDialog({ onClose: () => setShowCreateDialog(false) }) : nothing}
    </div>
  `;
});
